/**
 * PushPage — 推送管理（推送历史 / 推送模板 双 tab + 顶部 KPI + 创建主动推送）
 *
 * 1:1 移植自原型 design/Novel-admin/src/pages/push.jsx：JSX / className / inline-style 原样保留，
 * 仅做 JS→TS（加严格类型）+ window 全局引用 → ESM import 的机械转换：
 *   - window.NA_DATA → ~/data/queries 的冻结 hook（usePushTemplates / usePushHistoryPage）
 *   - window.SYS_CFG.getEnabledLanguages() → ~/data/queries 的 useSysConfig()（真数据派生启用语言数）
 *   - window.fmtLang → ~/lib/format 的 fmtLang
 *   - window.openDialog('push-task-detail' / 'template-detail' / 'test-send') → 本地 open state + import 对应弹窗
 *     （原型 'new-template' 未接：推送模板为 §2.5.2 server-seeded slug 闭集，admin 不新建模板，「新建模板」按钮禁用）
 *   - window.openDialog('confirm', ...) → ~/ui/Dialog 的 ConfirmDialog（通用二次确认）
 *   - window.toast → ~/ui/Toast 的 toast()
 *   - SearchInput / FilterButton / Pagination / DateRangeButton / ContextMenu / Select / Icons → ESM import
 */

import type {
  CreatePushTaskRequest,
  ListPushTasksQuery,
  PushTaskStatus,
  PushTaskType,
} from '@novel-admin/shared';
import { PUSH_TASK_STATUSES, PUSH_TEMPLATE_REGISTRY } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useAppScope } from '~/app/app-scope-store';
import { useApps, usePushHistoryPage, usePushTemplates, useSysConfig } from '~/data/queries';
import type { PushHistoryRow, PushTemplate } from '~/data/queries';
import { useBookLanguageRow } from '~/hooks/use-book-language-row';
import { pushApi } from '~/hooks/use-push';
import { fmtDateTime, fmtLang } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { formatPushTemplateName } from '~/lib/push-template-display';
import { useAuthStore } from '~/stores/auth-store';
import { BookLanguageRowPicker } from '~/ui/BookLanguageRowPicker';
import { ContextMenu } from '~/ui/ContextMenu';
import { DateRangeButton } from '~/ui/DatePicker';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { Pagination } from '~/ui/Pagination';
import { PaymentOfferPicker } from '~/ui/PaymentOfferPicker';
import { SearchInput } from '~/ui/SearchInput';
import { Select } from '~/ui/Select';
import { CardGridSkeleton, TableSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { PushAnalyticsPanel } from './PushAnalyticsPanel';
import { PushTaskDetail } from './dialogs/PushTaskDetail';
import { READ_ONLY_TEMPLATE_TITLE, TemplateDetail } from './dialogs/TemplateDetail';
import { TestSendDialog } from './dialogs/TestSendDialog';

// NOT-in-scope（契约无端点）按钮统一标注，指向 docs/plans 2026-06-12-web-real-api-integration-plan §五
const NOT_IN_SCOPE_TITLE =
  'NOT-in-scope · 契约无对应端点（见 docs/plans/2026-06-12-web-real-api-integration-plan §五）';

// 角色不足时写动作入口的统一禁用标注（前端 RBAC 防线；后端 requireRole 才是权威）
const FORBIDDEN_ROLE_TITLE = '权限不足 · 该操作受角色限制（后端 requireRole 为权威）';

// 打开数 / 开启率：真 API 无打开埋点字段 → 列渲染「--」并挂此标注，不显假 0 / 不显 NaN%
const NO_OPEN_RATE_TITLE = '未接入 · 推送打开埋点暂无对应统计端点';

// 范围筛选「全部 App」哨兵 value——映射为 target_scope=all_apps（不会与任何 App uuid 冲突）。
const SCOPE_ALL_APPS = '__all_apps__';

// 状态枚举（后端 PUSH_TASK_STATUSES 闭集）→ 中文 label（与 push-adapter STATUS_ZH 同口径，供状态筛选展示）。
const PUSH_STATUS_ZH: Record<PushTaskStatus, string> = {
  PENDING: '待发送',
  SENDING: '进行中',
  COMPLETED: '已完成',
  FAILED: '失败',
  CANCELLED: '已取消',
};

/**
 * push 写权限闭集（root/admin/operator）—— 后端 PUT /push/templates、POST /push/tasks 均 requireRole(root,admin,operator)。
 * 前端只做 UX 门控（禁用入口），后端 requireRole 才是权威（参照 users 的 useCanModerateUsers）。
 */
function useCanWritePush(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

export default function PushPage() {
  const { data: templates, isLoading: templatesLoading } = usePushTemplates();
  const canWrite = useCanWritePush();

  const [tab, setTab] = useState('history');
  const [showCompose, setShowCompose] = useState(false);

  // 替代原型 window.openDialog 的本地弹窗 open state
  const [taskDetail, setTaskDetail] = useState<PushHistoryRow | null>(null);
  const [tplDetail, setTplDetail] = useState<{ tpl: PushTemplate; edit?: boolean } | null>(null);
  const [testSend, setTestSend] = useState<PushTemplate | null>(null);

  // 页面 chrome + tabs 立即渲染——默认「历史」tab 不依赖模板查询，不能被慢 /push/templates 整页阻塞。
  // 模板查询只驱动「模板」tab 计数 + 模板页主体的局部加载态（见下方 PushTemplates 的 templatesLoading 门控）；
  // 历史 tab 的表格主体另由 PushHistory 内部 historyLoading 门控。
  const tabs = [
    { id: 'history', label: '推送历史', n: '分页' },
    { id: 'analytics', label: '数据分析', n: '消息 / 设备' },
    // 模板计数：加载中先留「…」，避免显示尚未到达的 0；到达后显真实条数。
    { id: 'templates', label: '推送模板', n: templatesLoading ? '…' : templates.length },
  ];

  return (
    // page-enter：数据到达后整页淡入（与原型 app.jsx 加载完成后的 page-enter 包裹一致）
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">推送管理</h1>
          {/* F4：投放量 / 打开率 / 营销关闭率无契约统计端点 → 不显杜撰生产数字，仅留页面职责说明 */}
          <div className="page-sub">
            推送历史 / 数据分析 / 推送模板管理 · 模板语言全集派生自{' '}
            <span className="mono">system_languages</span> (enabled+app_selectable)
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          <button type="button" className="btn" onClick={() => setTab('history')}>
            <Icons.IconHistory size={13} />
            触发日志
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={!canWrite}
            title={canWrite ? '' : FORBIDDEN_ROLE_TITLE}
            onClick={() => setShowCompose(true)}
          >
            <Icons.IconPlus size={13} />
            创建推送任务
          </button>
        </div>
      </div>

      {/* F4：移除顶部 5 张 KPI 卡（本月发送 12.84M / 送达率 / 打开率 / 转化率 / 营销关闭率）——
          真 API 无对应聚合统计端点（无埋点 / 无投放量端点），不渲染杜撰生产数字。
          接入推送投放统计端点后再补真值卡（记 docs/plans §五）。 */}

      <div style={{ borderBottom: '1px solid var(--border)', display: 'flex' }}>
        {tabs.map((t) => (
          <button
            type="button"
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              border: 0,
              background: 'transparent',
              padding: '9px 14px',
              font: 'inherit',
              fontSize: 12.5,
              cursor: 'pointer',
              color: tab === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
              fontWeight: tab === t.id ? 500 : 400,
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t.label}{' '}
            <span className="mono" style={{ color: 'var(--fg-faint)', fontSize: 11 }}>
              · {t.n}
            </span>
          </button>
        ))}
      </div>

      {tab === 'analytics' && <PushAnalyticsPanel />}
      {tab === 'history' && <PushHistory onOpenTask={(p) => setTaskDetail(p)} />}
      {tab === 'templates' && (
        <PushTemplates
          templates={templates}
          loading={templatesLoading}
          canWrite={canWrite}
          onOpenTemplate={(tpl, edit) => setTplDetail({ tpl, edit })}
          onTestSend={(tpl) => setTestSend(tpl)}
        />
      )}

      {showCompose && <ComposeDialog onClose={() => setShowCompose(false)} />}

      {taskDetail && <PushTaskDetail task={taskDetail} onClose={() => setTaskDetail(null)} />}
      {tplDetail && (
        <TemplateDetail
          tpl={tplDetail.tpl}
          edit={tplDetail.edit}
          canWrite={canWrite}
          onClose={() => setTplDetail(null)}
        />
      )}
      {testSend && (
        <TestSendDialog tpl={testSend} canWrite={canWrite} onClose={() => setTestSend(null)} />
      )}
    </div>
  );
}

// =========================================================
// 推送历史 — 搜索 / 多维筛选 / 分页表格
// =========================================================
function PushHistory({
  onOpenTask,
}: {
  onOpenTask: (p: PushHistoryRow) => void;
}) {
  const { data: apps } = useApps();
  // status / scope（app）现走服务端下推（ADM-PUSH-S005，P1 回归修复）：
  //   statuses 存后端枚举闭集值（PENDING/SENDING/COMPLETED/FAILED），FilterButton 用中文 label 展示；
  //   scopeSel 存范围选择——SCOPE_ALL_APPS 哨兵=全部 App（target_scope=all_apps），其余=具体 App uuid（app_id）。
  const [statuses, setStatuses] = useState<PushTaskStatus[]>([]);
  const [scopeSel, setScopeSel] = useState<string[]>([]);
  // types（任务类型 enum）/ ops（操作员 created_by uuid）现走服务端下推（跨全量过滤，非仅当前页）；
  // q（任务 ID / 类型 / 范围）因 push_tasks 无 title 列，仍为仅当前页客户端过滤（见 filtered 注释）。
  const [q, setQ] = useState('');
  const [types, setTypes] = useState<PushTaskType[]>([]);
  const [ops, setOps] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = useAppScope((s) => s.tablePageSize);

  // 服务端筛选集（ListPushTasksQuery）：范围选择映射为 target_scope / app_id 二选一——
  //   含「全部 App」哨兵 → target_scope=all_apps（all_apps 任务 target_app_id 为 null，与 app_id 互斥，取范围语义）；
  //   否则 → app_id=[选中的具体 App uuid]。status 直接透传枚举多选。
  //   task_type（任务类型 enum）/ operator（created_by uuid）同样服务端下推（inArray），跨全量过滤而非仅当前页。
  const filters = useMemo<ListPushTasksQuery>(() => {
    const f: ListPushTasksQuery = {};
    if (statuses.length) f.status = statuses;
    if (scopeSel.includes(SCOPE_ALL_APPS)) {
      f.target_scope = 'all_apps';
    } else if (scopeSel.length) {
      f.app_id = scopeSel;
    }
    if (types.length) f.task_type = types;
    if (ops.length) f.operator = ops;
    return f;
  }, [statuses, scopeSel, types, ops]);

  // offset 分页（ADM-PUSH-S005，cursor→offset）：page 直接下推后端，total 来自 envelope 真实总数 → 跳任意页。
  const {
    data: history,
    total: historyTotal,
    isLoading: historyLoading,
  } = usePushHistoryPage({
    page,
    limit: pageSize,
    filters,
  });

  // 本地分页偏好变化 → 回第一页（offset 下页码与 pageSize 绑定，换档须重算）。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 该 effect 专门在 pageSize 变化时重置页码。
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  // 越界页 clamp：后端可能返 data=[] 但 total>0（如 filter 收窄后停在旧页码）。加载完成后把 page clamp 回
  // maxPage 重取，避免操作员卡在空表。historyLoading 时 total 可能陈旧/0，跳过不误 clamp。
  useEffect(() => {
    if (historyLoading) return;
    const maxPage = Math.max(1, Math.ceil(historyTotal / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [historyTotal, pageSize, page, historyLoading]);

  // 服务端 filter 变化把页码重置回第一页（避免在旧页码越界到空页）。改 status / scope(app) 走此 reset。
  const resetToFirstPage = () => setPage(1);

  const canWrite = useCanWritePush();
  const queryClient = useQueryClient();
  // 取消推送任务（仅待发布态）：POST /push/tasks/:id/cancel → 非可取消态后端 PUSH_TASK_NOT_CANCELLABLE 错误 toast
  const cancelMutation = useMutation({
    mutationFn: (id: string) => pushApi.cancelTask(id),
    onSuccess: () => {
      toast('已取消推送任务', { tone: 'pos', icon: 'IconCheck' });
      queryClient.invalidateQueries({ queryKey: ['push-history'] });
    },
    onError: (err) =>
      toast(err instanceof Error ? err.message : '取消失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  // 导出单任务送达数据 CSV（裸流下载）
  const exportMutation = useMutation({
    mutationFn: (id: string) => pushApi.exportTaskDelivery(id),
    onSuccess: () => toast('已导出送达数据', { tone: 'pos', icon: 'IconDownload' }),
    onError: (err) =>
      toast(err instanceof Error ? err.message : '导出失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  // 复制任务：取任务详情（含 payload + target_app）→ 预填 ComposeDialog（操作员改排期后提交为新任务）。
  const [copyInitial, setCopyInitial] = useState<ComposeInitial | null>(null);
  const copyMutation = useMutation({
    mutationFn: (id: string) => pushApi.getTask(id),
    onSuccess: (task) =>
      setCopyInitial({
        type: task.task_type === 'TOPUP_PROMO' ? '充值活动' : '书籍推荐',
        scopeApp: task.target_app_id ?? '',
        bookId: typeof task.payload.book_id === 'string' ? task.payload.book_id : '',
        offerId: typeof task.payload.offer_id === 'string' ? task.payload.offer_id : '',
      }),
    onError: (err) =>
      toast(err instanceof Error ? err.message : '复制失败', { tone: 'neg', icon: 'IconAlert' }),
  });
  // 取消任务二次确认弹窗
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    tone?: 'danger' | 'warn';
    confirmLabel?: string;
    onConfirm: () => void;
  } | null>(null);

  const changeHistoryPage = (nextPage: number) => setPage(nextPage);

  // 当前服务端页（status / scope / task_type / operator 均已后端跨全量筛过）。
  //   q（任务 ID / 类型 / 范围）因 push_tasks 无 title 列、无服务端可搜目标 → 保留仅当前页客户端过滤。
  const all = history;
  const filtered = all.filter((p) => {
    if (q && !(p.id.includes(q) || p.type.includes(q) || p.scope.includes(q))) return false;
    return true;
  });
  const pageList = filtered;

  // 状态筛选用固定枚举闭集（不从当前页派生）：value=后端枚举，label=中文。
  const statusOptions = PUSH_TASK_STATUSES.map((s) => ({ value: s, label: PUSH_STATUS_ZH[s] }));
  // 范围筛选项来自 App 列表（非当前页派生）：「全部 App」哨兵 + 各具体 App（value=uuid，排除 'all' 聚合项）。
  const scopeOptions = [
    { value: SCOPE_ALL_APPS, label: '全部 App' },
    ...apps.filter((a) => a.id !== 'all').map((a) => ({ value: a.id, label: a.name })),
  ];
  // 操作员选项：value=created_by(admin_users.id，服务端按此 inArray 下推)，label=operator 昵称。
  //   选项集由已载入页去重派生（跨页 operator 发现需独立 operators 端点，留作低优先增强，见 plan NOT-in-scope）；
  //   但一旦选中，过滤即服务端跨全量生效（非仅当前页）。
  const opOptions = [...new Map(all.map((p) => [p.createdBy, p.operator])).entries()]
    .filter(([id, name]) => id && name)
    .map(([id, name]) => ({ value: id, label: name }));

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
        <SearchInput
          width={240}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder="任务 ID / 类型 / 范围"
        />
        <FilterButton
          label="任务类型"
          value={types}
          onChange={(next) => {
            // 服务端 filter（task_type inArray）：变化重置回第一页。options.value 全来自 MANUAL_PUSH_TYPES
            // 闭集（推送历史仅含运营主动创建的 BOOK_RECOMMEND/TOPUP_PROMO），断言回枚举闭集安全。
            setTypes(next as PushTaskType[]);
            setPage(1);
          }}
          options={[
            { value: 'BOOK_RECOMMEND', label: '书籍推荐' },
            { value: 'TOPUP_PROMO', label: '充值活动' },
          ]}
          width={150}
        />
        <FilterButton
          label="范围"
          value={scopeSel}
          onChange={(next) => {
            // 服务端 filter：scope(app) 变化必须重置游标回第一页（后端 cursor scope 绑 filter 集，否则 422）。
            setScopeSel(next);
            resetToFirstPage();
          }}
          options={scopeOptions}
          width={180}
          searchable
        />
        <FilterButton
          label="状态"
          value={statuses}
          onChange={(next) => {
            // 服务端 filter：status 变化必须重置游标回第一页（同 scope 的 cursor-scope 约束）。
            // FilterButton 给回 string[]，断言回后端枚举闭集（options.value 全来自 PUSH_TASK_STATUSES）。
            setStatuses(next as PushTaskStatus[]);
            resetToFirstPage();
          }}
          options={statusOptions}
          width={150}
        />
        <FilterButton
          label="操作员"
          value={ops}
          onChange={(next) => {
            setOps(next);
            setPage(1);
          }}
          options={opOptions}
          width={150}
        />
        <DateRangeButton defaultQuick="30d" />
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          当前页 {filtered.length} / {all.length} 条
        </span>
      </div>

      {/* ADM-PUSH-S005 · 本表仅含运营主动推送任务（逐字照 design/Novel-admin/src/pages/push.jsx:112-120）*/}
      <div className="card-flat" style={{ padding: '8px 12px', background: 'var(--bg-subtle)' }}>
        <div
          className="hstack"
          style={{ gap: 8, fontSize: 11.5, color: 'var(--fg-muted)', flexWrap: 'wrap' }}
        >
          <Icons.IconInfo size={11} style={{ color: 'var(--info)' }} />
          <span>
            推送历史仅展示运营
            <span className="strong" style={{ color: 'var(--fg)' }}>
              主动创建
            </span>
            的推送任务（书籍推荐 / 充值活动 · actor_type=ADMIN · 均为营销类，遵守用户营销开关）。
          </span>
          <span style={{ color: 'var(--fg-faint)' }}>·</span>
          <span>
            连载更新 / 续费成功等
            <span className="strong" style={{ color: 'var(--fg)' }}>
              功能类自动推送
            </span>
            由系统事件驱动，走 C 端 outbox→push_messages，
            <span className="mono">不进 admin push_tasks</span>，故不在此列表。
          </span>
        </div>
      </div>

      {/* 历史列表主体门控：active list query (usePushHistoryPage) isLoading → 表格骨架（与真实表同形 11 列、无勾选）；
          筛选行保持可交互、不被骨架遮盖。历史 tab 不依赖模板查询，慢 /push/templates 不阻塞本表。 */}
      {historyLoading ? (
        <TableSkeleton rows={11} cols={11} checkbox={false} />
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <Table className="tbl">
            <thead>
              <tr>
                <th>任务</th>
                <th>类型</th>
                <th>范围</th>
                <th className="num">受众</th>
                <th className="num">送达</th>
                <th className="num">打开</th>
                <th className="num">打开率</th>
                <th>状态</th>
                <th>触发时间</th>
                <th>操作员</th>
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {pageList.map((p) => (
                <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => onOpenTask(p)}>
                  <td className="mono">{p.id}</td>
                  <td>
                    <div className="hstack" style={{ gap: 6 }}>
                      <span
                        className={`chip ${p.category === '营销' ? 'chip-accent' : 'chip-info'}`}
                        style={{ fontSize: 10 }}
                      >
                        {p.category}
                      </span>
                      <span style={{ fontSize: 12 }}>{p.type}</span>
                    </div>
                  </td>
                  <td>
                    <span className="chip" style={{ fontSize: 10 }}>
                      {p.scope}
                    </span>
                  </td>
                  <td className="num">{p.audience.toLocaleString()}</td>
                  <td className="num">{p.delivered.toLocaleString()}</td>
                  {/* 打开数：真 API 无埋点 → opened=null 时渲染「--」（不显假 0）；开启率同理需 opened 且 delivered>0 才算（除零守卫，不显 NaN%）*/}
                  <td className="num">
                    {p.opened == null ? (
                      <span
                        className="mono"
                        style={{ color: 'var(--fg-faint)' }}
                        title={NO_OPEN_RATE_TITLE}
                      >
                        --
                      </span>
                    ) : (
                      p.opened.toLocaleString()
                    )}
                  </td>
                  <td className="num">
                    {p.opened != null && p.delivered > 0 ? (
                      `${((p.opened / p.delivered) * 100).toFixed(1)}%`
                    ) : (
                      <span
                        className="mono"
                        style={{ color: 'var(--fg-faint)' }}
                        title={NO_OPEN_RATE_TITLE}
                      >
                        --
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`chip ${p.status === '已完成' ? 'chip-pos' : p.status === '进行中' ? 'chip-info' : 'chip-warn'}`}
                      style={{ fontSize: 10 }}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {fmtDateTime(p.sentAt)}
                  </td>
                  <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {p.operator}
                  </td>
                  <td onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <ContextMenu
                      items={[
                        { label: '查看详情', icon: 'IconEye', onClick: () => onOpenTask(p) },
                        // 复制任务（接真）：取任务详情（含 payload + target_app）→ 预填 ComposeDialog → 操作员改排期后
                        // 提交为新任务（复用既有 createTask；纯前端组合，push_tasks 是 admin 独有表，无 C 端依赖）。
                        {
                          label: '复制任务',
                          icon: 'IconCopy' as const,
                          disabled: !canWrite || copyMutation.isPending,
                          title: canWrite ? undefined : FORBIDDEN_ROLE_TITLE,
                          onClick: () => copyMutation.mutate(p.id),
                        },
                        '---',
                        // 取消任务（接真）：仅待发送（PENDING）可取消 → POST /push/tasks/:id/cancel；二次确认
                        ...(p.status === '待发送'
                          ? [
                              {
                                label: '取消任务',
                                icon: 'IconBan' as const,
                                tone: 'danger' as const,
                                disabled: !canWrite || cancelMutation.isPending,
                                title: canWrite ? undefined : FORBIDDEN_ROLE_TITLE,
                                onClick: () =>
                                  setConfirm({
                                    title: '取消推送任务？',
                                    message: '取消后该任务不再发送（仅待发送任务可取消）。',
                                    tone: 'danger',
                                    confirmLabel: '取消任务',
                                    onConfirm: () => cancelMutation.mutate(p.id),
                                  }),
                              },
                            ]
                          : []),
                        // 导出任务送达数据（接真）：GET /push/tasks/:id/export → downloadAuthedCsv
                        {
                          label: '导出数据',
                          icon: 'IconDownload' as const,
                          disabled: exportMutation.isPending,
                          onClick: () => exportMutation.mutate(p.id),
                        },
                      ]}
                    >
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        style={{ width: 22, height: 22 }}
                      >
                        <Icons.IconMoreV size={12} />
                      </button>
                    </ContextMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination
            total={historyTotal}
            page={page}
            pageSize={pageSize}
            onChange={changeHistoryPage}
            totalMode="exact"
          />
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm.title}
          message={confirm.message}
          tone={confirm.tone}
          confirmLabel={confirm.confirmLabel}
          onConfirm={() => confirm.onConfirm()}
          onClose={() => setConfirm(null)}
        />
      )}
      {/* 复制任务：预填的 ComposeDialog（提交为新任务，复用既有创建流） */}
      {copyInitial && <ComposeDialog initial={copyInitial} onClose={() => setCopyInitial(null)} />}
    </div>
  );
}

// =========================================================
// 推送模板 — 系统预置闭集说明 + 按类别分组卡片
// =========================================================
function PushTemplates({
  templates,
  loading,
  canWrite,
  onOpenTemplate,
  onTestSend,
}: {
  templates: PushTemplate[];
  // 模板查询 isLoading（由 PushPage 透传）：仅 gate 本 tab 的模板卡片网格主体，不阻塞整页 / 历史 tab。
  loading: boolean;
  canWrite: boolean;
  onOpenTemplate: (tpl: PushTemplate, edit?: boolean) => void;
  onTestSend: (tpl: PushTemplate) => void;
}) {
  // 启用语言数（缺语言提示用）：useSysConfig 真数据派生（system_languages WHERE enabled），不读 mock seed。
  const { data: sys } = useSysConfig();
  const enabledLangCount = sys.languages.filter((l) => l.enabled).length;
  // 按类别分组（营销 / 功能）
  const groups = templates.reduce<Record<string, PushTemplate[]>>((acc, t) => {
    const k = t.category;
    const arr = acc[k] ?? [];
    arr.push(t);
    acc[k] = arr;
    return acc;
  }, {});
  // 横幅类型清单按 §2.5.2 注册表镜像派生（ADM-PUSH-S001 不写死条数）：上游加 slug 时计数与名称随之更新
  const registrySlugs = Object.keys(PUSH_TEMPLATE_REGISTRY);
  const marketingNames = registrySlugs
    .filter((slug) => !PUSH_TEMPLATE_REGISTRY[slug]?.isFunctional)
    .map(formatPushTemplateName);
  const functionalNames = registrySlugs
    .filter((slug) => PUSH_TEMPLATE_REGISTRY[slug]?.isFunctional)
    .map(formatPushTemplateName);
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div
        className="card"
        style={{ padding: 14, background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconShield size={14} style={{ color: 'var(--info)', marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.55 }}>
            <span style={{ fontWeight: 500 }}>ADM-PUSH-S001 · 推送类型为系统预置闭集</span>
            <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>
              共 {registrySlugs.length} 类（营销 {marketingNames.length} · 功能{' '}
              {functionalNames.length}）· 不支持新增类型 ·
              仅可维护各类型的模板与多语言文案（单用户服务通知按收件人逐条确认，模板只读）
            </span>
            <div className="hstack" style={{ gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
              <span className="chip chip-warn" style={{ fontSize: 10 }}>
                营销 {marketingNames.length} 类：{marketingNames.join(' · ')}
              </span>
              <span className="chip chip-info" style={{ fontSize: 10 }}>
                功能 {functionalNames.length} 类：{functionalNames.join(' · ')}
              </span>
            </div>
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--fg-muted)', lineHeight: 1.55 }}>
              <span className="strong" style={{ color: 'var(--fg)' }}>
                多语言维护范围
              </span>
              ：模板应覆盖系统预置 App 可选运行语言全集（
              <span className="mono">system_languages WHERE enabled AND app_selectable</span>
              ），而非仅当前 App 语言 · 中文和英文版本必填 · 未维护语言渲染时 fallback 英文 · 渲染为{' '}
              <span className="mono">user-level 单次渲染</span>
              （users.last_seen_language_per_app → en，同用户全设备统一语言）
            </div>
          </div>
        </div>
      </div>
      <div className="hstack" style={{ gap: 8 }}>
        <SearchInput width={240} placeholder="搜索模板…" />
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          共 {loading ? '…' : templates.length} 个模板
        </span>
        {/* 新建模板：契约模板是 §2.5.2 baseline slug 闭集、admin 不可新建 slug（PUSH_TEMPLATE_REGISTRY）→ NOT-in-scope（§五）*/}
        <button
          type="button"
          className="btn btn-accent btn-sm"
          disabled
          title={`${NOT_IN_SCOPE_TITLE} · 模板为 baseline slug 闭集，admin 不可新建`}
        >
          <Icons.IconPlus size={11} />
          新建模板
        </button>
      </div>
      {/* 模板网格主体局部加载态：本 tab 自有的卡片骨架（minWidth 对齐真实网格的 minmax(360px,1fr)），
          仅在切到本 tab 且模板查询未就绪时显示——不阻塞整页 chrome / 默认历史 tab */}
      {loading && <CardGridSkeleton minWidth={360} />}
      {!loading &&
        Object.entries(groups).map(([cat, list]) => (
          <div key={cat}>
            <div className="hstack" style={{ gap: 6, marginBottom: 6, padding: '4px 0' }}>
              <span style={{ fontWeight: 500, fontSize: 12 }}>{cat}类推送</span>
              <span className="mono muted" style={{ fontSize: 11 }}>
                · {list.length}
              </span>
              {cat === '营销' && (
                <span className="chip chip-warn" style={{ fontSize: 10 }}>
                  受营销开关影响
                </span>
              )}
              {cat === '功能' && (
                <span className="chip chip-info" style={{ fontSize: 10 }}>
                  不受营销开关影响
                </span>
              )}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
                gap: 12,
              }}
            >
              {list.map((t) => (
                <div
                  key={t.id}
                  className="card"
                  style={{
                    padding: 14,
                    cursor: 'pointer',
                    transition: 'border-color 0.12s, box-shadow 0.12s',
                  }}
                  onClick={() => onOpenTemplate(t)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-strong)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div className="hstack" style={{ marginBottom: 8, gap: 8 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: cat === '营销' ? 'var(--accent-soft)' : 'var(--info-soft)',
                        color: cat === '营销' ? 'var(--accent)' : 'var(--info)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {cat === '营销' ? (
                        <Icons.IconFlame size={14} />
                      ) : (
                        <Icons.IconBell size={14} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: 12.5 }}>{t.type}</div>
                      <div className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                        {t.id}
                      </div>
                    </div>
                    <div
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <ContextMenu
                        items={[
                          {
                            label: '查看模板',
                            icon: 'IconEye',
                            onClick: () => onOpenTemplate(t),
                          },
                          // 编辑模板：upsertTemplate(root/admin/operator)；viewer 与确认快照来源模板禁用
                          {
                            label: '编辑模板',
                            icon: 'IconEdit',
                            disabled: !canWrite || t.readOnly,
                            title: t.readOnly
                              ? READ_ONLY_TEMPLATE_TITLE
                              : canWrite
                                ? undefined
                                : FORBIDDEN_ROLE_TITLE,
                            onClick: () => onOpenTemplate(t, true),
                          },
                          // 复制模板：复制 = 新建自定义 slug，与 §2.5.2 slug 闭集冲突（admin 不可新建）→ NOT-in-scope（§五）
                          {
                            label: '复制模板',
                            icon: 'IconCopy',
                            disabled: true,
                            title: NOT_IN_SCOPE_TITLE,
                          },
                          '---',
                          // 测试发送（ADM-PUSH-S006）：弹窗内按可测集 / 启停 / 角色门控发送按钮
                          {
                            label: '测试发送',
                            icon: 'IconPaperPlane',
                            onClick: () => onTestSend(t),
                          },
                          '---',
                          // 删除模板：baseline slug 禁删（数据字典 §2.5.2 + DB trigger 兜底）→ NOT-in-scope（§五）
                          {
                            label: '删除模板',
                            icon: 'IconTrash',
                            tone: 'danger',
                            disabled: true,
                            title: `${NOT_IN_SCOPE_TITLE} · baseline 模板禁删`,
                          },
                        ]}
                      >
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          style={{ width: 24, height: 24 }}
                        >
                          <Icons.IconMoreV size={12} />
                        </button>
                      </ContextMenu>
                    </div>
                  </div>
                  <div
                    style={{
                      padding: '8px 10px',
                      background: 'var(--bg-subtle)',
                      borderRadius: 4,
                      fontSize: 11.5,
                      lineHeight: 1.5,
                    }}
                  >
                    <div className="hstack" style={{ gap: 5, marginBottom: 3 }}>
                      <span className="mono chip" style={{ fontSize: 9, padding: '0 5px' }}>
                        ZH
                      </span>
                      <span>{t.displayTitle}</span>
                    </div>
                    <div className="hstack" style={{ gap: 5 }}>
                      <span className="mono chip" style={{ fontSize: 9, padding: '0 5px' }}>
                        EN
                      </span>
                      <span style={{ color: 'var(--fg-muted)' }}>{t.originalTitle}</span>
                    </div>
                  </div>
                  <div className="hstack" style={{ marginTop: 10, gap: 6, flexWrap: 'wrap' }}>
                    {t.langs.map((l) => (
                      <span key={l} className="chip" style={{ fontSize: 9.5, padding: '0 5px' }}>
                        {fmtLang(l)}
                      </span>
                    ))}
                    {(() => {
                      const target = enabledLangCount;
                      const have = Math.min(t.langs.length, target);
                      return (
                        have < target && (
                          <span
                            className="mono"
                            style={{ fontSize: 10, color: 'var(--warn)' }}
                            title="未维护语言 fallback 英语"
                          >
                            缺 {target - have} / {target} 语言
                          </span>
                        )
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

// =========================================================
// 创建主动推送任务弹窗 — 仅「书籍推荐」「充值活动」两类
// =========================================================
// UUID 形态校验（book_id 契约要求 uuid；提交前本地校验，避免明知 422 仍发请求）
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * 定时发送时刻处理（F1 + F3）——零时区手算，全程交给原生 Date。
 *
 * Why（F3 修正 r1）：scheduled_at 是**绝对时刻入参**（50-命名与类型规范 §2.2.1；非章节定时发布那种「墙上时刻」
 * 例外字段）。r1 手算 Europe/Berlin offset 在 DST 边界会算错侧（春跳不存在时刻、秋回歧义时刻），且发出 `+02:00`
 * 数字 offset 被 server `z.string().datetime()`（无 offset:true）拒（F1）。改用原生 `datetime-local` 输入
 * （运营**浏览器本地时区**录入墙上时刻），`new Date(localValue)` 由浏览器按其自身 tzdata 正确解释（含 DST，无手算），
 * `.toISOString()` 归一为 UTC `Z`。确认区显式展示「将发送的绝对 UTC 时刻 + 你的本地时刻」让运营确认。
 *
 * 调度时区语义是契约缺口（dev-docs 未规定主动推送 scheduled_at 参照时区 / 跨运营 tz 一致性），
 * 已记账 docs/plans/2026-06-12-web-real-api-integration-plan §五：现取运营浏览器本地 tz → UTC 绝对时刻
 * + UI 显式展示绝对时刻为诚实 interim；统一参照 tz 需另起 dev-docs PR。
 */

/** `datetime-local` 值（YYYY-MM-DDTHH:mm，无 offset = 浏览器本地墙上时刻）→ UTC `Z` 绝对 ISO；非法值返 null。 */
function localInputToUtcIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local); // 浏览器按自身本地 tz（含 DST）解释墙上时刻，无手算
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** 该绝对时刻是否在过去（含等于当前的边界算过去，拒绝立即过期的排期）。 */
function isoIsInPast(iso: string): boolean {
  return new Date(iso).getTime() <= Date.now();
}

/** 当前浏览器本地时刻格式化为 `datetime-local` value（YYYY-MM-DDTHH:mm），作默认排期值。 */
function nowLocalInput(): string {
  const now = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}T${p(now.getHours())}:${p(now.getMinutes())}`;
}

/** 把 UTC ISO 渲染成「YYYY-MM-DD HH:mm UTC」+ 运营本地时刻，供确认区显式展示绝对时刻。 */
function describeScheduled(iso: string): { utc: string; local: string } {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  const utc = `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
  const local = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  return { utc, local };
}

/**
 * 主动推送 task_type（中文 label）→ 设计稿模板 slug 映射（P-120 只读说明行用）。
 * 模板由 task_type 唯一决定、运营不可手选：书籍推荐（BOOK_RECOMMEND）= tpl_recomm、
 * 充值活动（TOPUP_PROMO）= tpl_charge。slug 为设计稿/mock 的 id 形态，接真后 tpl.id 是 UUID、
 * 不可靠，故 reverse-find 以 adapter 稳定保证的 tpl.type（中文 label，= 本 map key）为主键、slug 作 mock 兜底。
 */
const TEMPLATE_SLUG_BY_TASK_TYPE: Record<'书籍推荐' | '充值活动', string> = {
  书籍推荐: 'tpl_recomm',
  充值活动: 'tpl_charge',
};

/** 复制任务预填值（ADM-PUSH-S003）：从既有任务派生，操作员再编辑（尤其须重设排期）后提交为新任务。 */
type ComposeInitial = {
  type: '书籍推荐' | '充值活动';
  scopeApp: string;
  bookId: string;
  offerId: string;
};

function ComposeDialog({ onClose, initial }: { onClose: () => void; initial?: ComposeInitial }) {
  const { data: apps } = useApps();
  const { data: templates } = usePushTemplates();
  const queryClient = useQueryClient();
  // '书籍推荐' = BOOK_RECOMMEND；'充值活动' = TOPUP_PROMO（ADM-PUSH-S003 仅这两类可主动创建）。复制任务时用 initial 预填。
  const [type, setType] = useState<'书籍推荐' | '充值活动'>(initial?.type ?? '书籍推荐');
  // 运营主动推送一律 single_app（张培 #244 决议 1：各 App 支付产品 / 书籍语言 App-scoped）→ 创建态无 all_apps 选项，必选目标 App。
  const [scopeApp, setScopeApp] = useState(initial?.scopeApp ?? '');
  // 书籍推荐 payload.book_id（契约 uuid）；充值活动 payload.offer_id（app_payment_offers.id 运营支付活动实例，#228 决议 3）
  const [bookId, setBookId] = useState(initial?.bookId ?? '');
  // 目标 App 的可用语言：书候选的语言范围（ADM-PUSH-S003，见下方 picker 注释）。
  // 未选 App → undefined = 不限定。
  //
  // **取 allowed_languages 原样，不叠 `∪ {'en'}` 的 runtime_languages**：dev-docs
  // `03-数据字典/20-内容.md` §7.0「英语行恒为成员」那条兜底，原文限定了消费面 ——
  // 「供 admin 投放名单 / end-recommendations 候选 / novel.delisted fanout 等**非 C 端选行**
  // 消费面……**C 端内容选行不消费该兜底** —— 按 §7.1 精确取行，`en ∉ allowed_languages`
  // 的 App 下英语行不会被任何 C 端内容路径选中」，其消费面表也不含推送任务。
  // 推送是发给终端用户、点开即走 C 端选行的深链：对 en ∉ allowed_languages 的 App 推英语书，
  // 正是 ADM-PUSH-S003 要挡的「给 A 语言 App 推 B 语言书」。
  const targetAppLanguages = useMemo(() => {
    const target = apps.find((a) => a.id === scopeApp.trim());
    // AppRow.lang 是 AppResponse.allowed_languages 的直接映射（apps-adapter）
    const langs = target?.lang;
    return langs && langs.length > 0 ? langs : undefined;
  }, [apps, scopeApp]);
  // 已选书行详情（picker 展示封面 / 书名 / 语言用）
  const selectedPushBookQ = useBookLanguageRow(bookId);
  const [offerId, setOfferId] = useState(initial?.offerId ?? '');
  const [offerSelectionBlocked, setOfferSelectionBlocked] = useState(false);
  // 内容预览语言（仅影响预览展示，不进请求）
  const [previewLang, setPreviewLang] = useState<'en' | 'zh'>('en');
  // 定时发送面板 — 原生 datetime-local（浏览器本地墙上时刻），默认取「当前本地时刻」（不硬编码固定日）
  const [schedule, setSchedule] = useState(false);
  const [schedLocal, setSchedLocal] = useState(() => nowLocalInput());

  // 模板预览样例文案（mock 文案 + 变量样例填充，与原型 ComposeDialog 一致）
  const sampleTitle =
    type === '书籍推荐'
      ? previewLang === 'zh'
        ? '为你推荐 · Pact of the Abyss'
        : 'Recommended for you · Pact of the Abyss'
      : previewLang === 'zh'
        ? '限时特惠 · 充 €9.99 多送 30%'
        : 'Limited offer · top up €9.99 get 30% more';

  // P-120 只读说明行用：按当前 task_type 反查将使用的模板（type 为 adapter 稳定保证的 join key，
  // id 仅 mock 形态兜底）；接真模板缺失（如 server 未 seed）时 tpl 为 undefined → 不渲染只读行，不杜撰。
  const tplDesignSlug = TEMPLATE_SLUG_BY_TASK_TYPE[type];
  const tpl = templates.find((t) => t.type === type || t.id === tplDesignSlug);

  // payload / scope 完整性校验（书籍推荐需合法 uuid book_id；充值活动需合法 uuid offer_id，server 再校验可服务性）
  const payloadReady =
    type === '书籍推荐' ? UUID_RE.test(bookId.trim()) : UUID_RE.test(offerId.trim());
  // single_app：必选目标 App（创建态无 all_apps）
  const scopeReady = scopeApp.trim().length > 0;

  // 定时排期（F1+F3）：浏览器本地墙上时刻 → UTC `Z` 绝对 ISO（new Date 由浏览器按自身 tz/DST 解释）；立即发送 → null
  const scheduledIso = schedule ? localInputToUtcIso(schedLocal) : null;
  // 排期值合法性：开了定时但解析不出（空 / 非法）→ 视为未就绪；过去（含等于当前）→ 禁用提交
  const scheduleInvalid = schedule && scheduledIso == null;
  const schedulePast = scheduledIso != null && isoIsInPast(scheduledIso);
  const scheduleReady = !schedule || (!scheduleInvalid && !schedulePast);
  // 确认区显式展示将发送的绝对 UTC 时刻 + 运营本地时刻（让运营确认，诚实 interim）
  const scheduledDesc = scheduledIso && !schedulePast ? describeScheduled(scheduledIso) : null;

  const createMutation = useMutation({
    mutationFn: (body: CreatePushTaskRequest) => pushApi.createTask(body),
    onSuccess: async () => {
      toast(
        schedule && scheduledDesc
          ? `已创建定时推送 · ${scheduledDesc.utc} 发送`
          : '推送任务已创建 · 正在发送',
        {
          tone: 'pos',
          icon: schedule ? 'IconClock' : 'IconPaperPlane',
        },
      );
      await queryClient.invalidateQueries({ queryKey: ['push-history'] });
      onClose();
    },
    onError: (err) => {
      // AdminApiError.message 已按 code 本地化（error-messages.ts）；非 AdminApiError 回退通用文案
      toast(err instanceof AdminApiError ? err.message : '创建推送任务失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  // 组装 CreatePushTaskRequest（按 task_type 分形 payload + scope + 可选 scheduled_at）
  const submit = () => {
    if (!payloadReady || !scopeReady || !scheduleReady) return;
    const base = {
      // 运营主动推送一律 single_app（#244 决议 1）：target_scope 恒 'app' + 必选 target_app_id
      target_scope: 'app' as const,
      target_app_id: scopeApp,
      // 定时：浏览器本地墙上时刻 → UTC `Z` 绝对 ISO（契约 datetime 只收 Z）；立即发送 → null
      scheduled_at: scheduledIso,
    };
    const body: CreatePushTaskRequest =
      type === '书籍推荐'
        ? { task_type: 'BOOK_RECOMMEND', payload: { book_id: bookId.trim() }, ...base }
        : { task_type: 'TOPUP_PROMO', payload: { offer_id: offerId.trim() }, ...base };
    createMutation.mutate(body);
  };

  const pending = createMutation.isPending;
  // 书籍推荐必须满足「书行语言 ∈ 目标 App 的 allowed_languages」（ADM-PUSH-S003 · 张培 #244
  // 决议 1「不给 A 语言 App 推 B 语言书」）。picker 只筛**未来**候选：选完书再改目标 App 时旧
  // bookId 仍留在 state 里，顶部只打一个「语言不匹配」的标，而 payloadReady 只验 UUID。
  //
  // **这条闸 fail-closed（核不出就拦），与 offer 侧刻意相反**：offer 被写侧同一判据兜着，
  // 前端放过去最多吃一个 422、可重试，所以那边宁可不拦；书籍这条**服务端根本不校验**
  // （`createTask` 只当 payload 收 UUID），放过去就是一条已投递、收不回来的坏推送 ——
  // 此刻整个系统只有这里在守。「复制任务」预填 App + 书、两个查询都还在飞的那一瞬，
  // 正是最容易踩中的窗口。
  const bookScopeState: 'ok' | 'mismatch' | 'unverified' =
    type !== '书籍推荐' || !bookId.trim()
      ? 'ok'
      : // App 语言集取不到（apps 未加载 / App 未命中 / allowed_languages 为空）或书行详情
        // 未成功查到 → 无判据，一律按未核过处理
        !targetAppLanguages || !selectedPushBookQ.isSuccess || !selectedPushBookQ.data
        ? 'unverified'
        : targetAppLanguages.includes(selectedPushBookQ.data.locale)
          ? 'ok'
          : 'mismatch';
  const bookLocaleBlocked = bookScopeState !== 'ok';
  // 禁用提交：payload/scope 不完整、排期在过去、或正在提交
  // 两个 picker 各自上报「按目标 App 判这一项会被写侧拒 / 不该发」时一并锁住提交
  const submitDisabled =
    !payloadReady ||
    !scopeReady ||
    !scheduleReady ||
    pending ||
    bookLocaleBlocked ||
    (type !== '书籍推荐' && offerSelectionBlocked);
  const submitTitle = !payloadReady
    ? type === '书籍推荐'
      ? '请选择要推荐的书籍语言行'
      : '请选择要推广的运营活动'
    : !scopeReady
      ? '请选择目标 App'
      : bookScopeState === 'mismatch'
        ? '所选书行的语言不在目标 App 的可用语言内 —— 请改选该 App 语言的书行'
        : bookScopeState === 'unverified'
          ? '正在核对书行语言与目标 App 的可用语言；核不出时不放行（服务端不校验这条，只能在此拦）'
          : scheduleInvalid
            ? '请选择有效的定时发送时间'
            : !scheduleReady
              ? '定时发送时间不能早于当前'
              : '';

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="dialog" style={{ width: 680 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div className="hstack" style={{ gap: 8 }}>
            <Icons.IconPaperPlane size={15} style={{ color: 'var(--accent)' }} />
            <div style={{ fontWeight: 500, fontSize: 14 }}>创建主动推送任务</div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>
            仅支持「书籍推荐」「充值活动」两类主动推送 · 均为营销类，遵守用户营销开关
          </div>
        </div>
        <div
          style={{
            padding: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            maxHeight: '62vh',
            overflow: 'auto',
          }}
        >
          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              推送类型
            </div>
            <div className="hstack" style={{ gap: 8 }}>
              {(['书籍推荐', '充值活动'] as const).map((t) => (
                <button
                  type="button"
                  key={t}
                  onClick={() => setType(t)}
                  className="focusable"
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 6,
                    border: type === t ? '1px solid var(--accent)' : '1px solid var(--border)',
                    background: type === t ? 'var(--accent-soft)' : 'var(--bg-elev)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    color: 'var(--fg)',
                  }}
                >
                  <div className="hstack" style={{ gap: 8 }}>
                    {t === '书籍推荐' ? (
                      <Icons.IconBook size={14} />
                    ) : (
                      <Icons.IconFlame size={14} />
                    )}
                    <span style={{ fontWeight: 500, fontSize: 12.5 }}>{t}</span>
                    <span className="chip chip-warn" style={{ fontSize: 9 }}>
                      营销类
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4 }}>
                    {t === '书籍推荐' ? '向用户推送指定书籍' : '向用户推送指定运营支付活动'}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              推送范围
            </div>
            {/* 运营主动推送一律 single_app（张培 #244 决议 1）：创建态无「全部 App」选项，必选单个目标 App */}
            <Select
              className="input"
              value={scopeApp}
              onChange={(e) => setScopeApp(e.target.value)}
              style={{ maxWidth: 280 }}
            >
              <option value="" disabled>
                选择目标 App…
              </option>
              {apps
                .filter((a) => a.id !== 'all')
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.id}
                  </option>
                ))}
            </Select>
            <div style={{ marginTop: 5, fontSize: 10.5, color: 'var(--fg-faint)' }}>
              运营主动推送仅支持单个 App（各 App 支付产品 / 书籍语言不同，不跨 App 推送）
            </div>
          </div>
          {type === '书籍推荐' ? (
            <div>
              <div className="label" style={{ marginBottom: 6 }}>
                书编码
              </div>
              {/* payload.book_id 契约是 **uuid**（A4 §3.2），而这里原先的 placeholder 写「书编码（10 位）」——
                  运营照着输 10 位编码，`UUID_RE` 校验必失败且不给理由。改用统一 picker，值由它给出。
                  **不锁语言**：推送面向该 App 全体用户、语言各自由 `users.last_seen_language_per_app` 决定，
                  契约也未要求 book_id 与用户语言匹配（与 banner slide 的「位语言即帧语言」不同）。 */}
              <div style={{ maxWidth: 420 }}>
                <BookLanguageRowPicker
                  value={bookId}
                  book={selectedPushBookQ.data}
                  onChange={setBookId}
                  // 候选限定在目标 App 的 allowed_languages 内 —— 依据是需求原文而非技术推断：
                  // ADM-PUSH-S003（张培 #244 决议 1）明写「支付产品 / 书籍语言均 App-scoped、
                  // 不跨 App 共用入口**或给 A 语言 App 推 B 语言书**」。服务端目前只把 payload
                  // 当 UUID 校验、不查语言成员关系，所以这条口径此刻只有 picker 在守。
                  // 未选 App 时不限定（此时 scopeReady=false，本来就提交不了）。
                  lockedLocale={targetAppLanguages}
                  bookLookupFailed={
                    selectedPushBookQ.isError ||
                    (selectedPushBookQ.isSuccess && !selectedPushBookQ.data)
                  }
                />
              </div>
            </div>
          ) : (
            <div>
              <div className="label" style={{ marginBottom: 6 }}>
                运营活动 offer_id
              </div>
              {/* #228 决议 3：充值活动按 offer_id 寻址运营支付活动实例（app_payment_offers.id），
                  裸 UUID 输入对齐 banner NewSlideDialog offer 体例；server 写侧按目标 App + 投递时刻校验可服务性。 */}
              {/* 与 banner slide 共用同一个 picker。判据按 A4 §3.3：
                  `app_id = target_app_id ∧ is_active ∧ 未软删 ∧ 投递时刻 ∈ [starts_at, ends_at)`。
                  **投递时刻是 `scheduled_at ?? now`、不是 now** —— 定时推送若按 now 判，
                  会挡掉「发送时才生效」的活动，或放进一个届时已过期的。 */}
              <div style={{ maxWidth: 420 }}>
                <PaymentOfferPicker
                  value={offerId}
                  appId={scopeApp.trim() || null}
                  // 推送没有 banner 的「草稿」那一档：A4 §3.3 建任务即校验，故恒严格
                  strictWindow
                  evaluateAt={scheduledIso ? new Date(scheduledIso) : undefined}
                  // 改 App / 改排期后所选活动可能落到窗口外：候选会把它筛掉但 offerId 还在，
                  // 不接这个回调的话「确认」仍可点，POST 必吃 PUSH_OFFER_NOT_FOUND
                  onSelectionBlocked={setOfferSelectionBlocked}
                  context="push-task"
                  onChange={setOfferId}
                />
              </div>
              <div style={{ marginTop: 5, fontSize: 10.5, color: 'var(--fg-faint)' }}>
                运营支付活动实例 ID（入口携带 ?offer_id=）· 须为该 App 下投递时刻生效中的活动
              </div>
            </div>
          )}
          {/* P-120 · 推送模板只读说明行 — 模板由 task_type 唯一决定、不可手选（无交互 Select，仅展示） */}
          {tpl && (
            <div>
              <div className="label" style={{ marginBottom: 6 }}>
                推送模板
              </div>
              <div style={{ fontSize: 12.5 }}>
                将使用模板 <span className="mono strong">{tpl.id}</span> · {tpl.type}
              </div>
              <div style={{ marginTop: 5, fontSize: 10.5, color: 'var(--fg-faint)' }}>
                模板由 task_type 唯一决定，不可手选
              </div>
            </div>
          )}
          {/* 内容预览 · 目标语言实际渲染 */}
          <div>
            <div className="hstack" style={{ marginBottom: 6 }}>
              <span className="label">内容预览</span>
              <div style={{ flex: 1 }} />
              <div className="seg">
                {(['en', 'zh'] as const).map((l) => (
                  <button
                    type="button"
                    key={l}
                    className={previewLang === l ? 'on' : ''}
                    onClick={() => setPreviewLang(l)}
                  >
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-flat" style={{ padding: 12, background: 'var(--bg-subtle)' }}>
              <div className="hstack" style={{ gap: 10 }}>
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 7,
                    background: 'var(--accent-soft)',
                    color: 'var(--accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icons.IconBell size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500 }}>{sampleTitle}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
                    {previewLang === 'zh'
                      ? '点击查看详情 · 变量已按样例填充'
                      : 'Tap to view · variables filled with samples'}
                  </div>
                </div>
                <span className="chip mono" style={{ fontSize: 9.5 }}>
                  {previewLang.toUpperCase()}
                </span>
              </div>
            </div>
            <div style={{ marginTop: 5, fontSize: 10.5, color: 'var(--fg-faint)' }}>
              按模板在目标语言下的实际渲染 · 未维护语言的用户将收到英文版
            </div>
          </div>
          {/* 营销过滤说明（书籍推荐 / 充值活动均为营销类）*/}
          <div
            className="card-flat"
            style={{
              padding: '10px 12px',
              background: 'var(--warn-soft)',
              borderColor: 'transparent',
            }}
          >
            <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
              <Icons.IconAlert size={12} style={{ color: 'var(--warn)', marginTop: 2 }} />
              <span style={{ fontSize: 11.5, lineHeight: 1.55 }}>
                营销类推送遵守用户的营销通知开关；已禁用营销通知的用户将不收到此推送（过滤由服务端按
                ADM-PUSH-S004 执行）· 营销类可在「推送模板」页由运营启停
              </span>
            </div>
          </div>
          {/* 预计受众 · 分层漏斗 — 无受众预估端点 → 各档显「—」，不展示编造数字（实际受众由服务端在任务执行时计算）*/}
          <div>
            <div className="hstack" style={{ marginBottom: 6, gap: 6 }}>
              <span className="label">预计受众 · 分层</span>
              <span
                className="chip"
                style={{ fontSize: 9.5 }}
                title="未接入 · 暂无受众预估端点，实际受众与过滤由服务端在任务执行时计算"
              >
                未接入
              </span>
            </div>
            <div className="card-flat" style={{ padding: 12, background: 'var(--bg-subtle)' }}>
              <div className="hstack" style={{ gap: 12 }}>
                {(
                  [
                    { v: '—', l: '触达用户（该范围全量）' },
                    { v: '—', l: '有效推送 token' },
                    { v: '—', l: '营销开关过滤后' },
                    { v: '—', l: '预计打开' },
                  ] as { v: string; l: string }[]
                ).map((s, i, arr) => (
                  <Fragment key={s.l}>
                    <div>
                      <div
                        className="mono"
                        style={{ fontSize: 17, fontWeight: 600, color: 'var(--fg-faint)' }}
                      >
                        {s.v}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>{s.l}</div>
                    </div>
                    {i < arr.length - 1 && (
                      <Icons.IconChevRight size={12} style={{ color: 'var(--fg-faint)' }} />
                    )}
                  </Fragment>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--fg-faint)' }}>
                暂无受众预估端点，实际受众与过滤由服务端在任务执行时计算（各档待接入后填充）
              </div>
            </div>
          </div>
          {/* 定时发送 · 原生 datetime-local（浏览器本地墙上时刻），确认区显式展示将发送的绝对 UTC 时刻 */}
          {schedule && (
            <div className="card-flat" style={{ padding: 12 }}>
              <div className="label" style={{ marginBottom: 8 }}>
                定时发送时间
              </div>
              <div className="hstack" style={{ gap: 8 }}>
                <input
                  type="datetime-local"
                  className="input mono"
                  value={schedLocal}
                  onChange={(e) => setSchedLocal(e.target.value)}
                  style={{ width: 230 }}
                />
                <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  按你的浏览器本地时区录入
                </span>
              </div>
              {/* 显式展示将发送的绝对时刻（UTC）+ 本地时刻，让运营确认（诚实 interim — 调度参照 tz 是契约缺口）*/}
              {scheduledDesc && (
                <div
                  className="hstack"
                  style={{ gap: 6, marginTop: 8, fontSize: 11, color: 'var(--fg-muted)' }}
                >
                  <Icons.IconClock size={11} />
                  <span>
                    将于 <span className="mono strong">{scheduledDesc.utc}</span> 发送（你的本地{' '}
                    <span className="mono">{scheduledDesc.local}</span>）
                  </span>
                </div>
              )}
              {scheduleInvalid && (
                <div
                  className="hstack"
                  style={{ gap: 6, marginTop: 8, fontSize: 11, color: 'var(--neg)' }}
                >
                  <Icons.IconAlert size={11} />
                  <span>请选择有效的定时发送时间</span>
                </div>
              )}
              {schedulePast && (
                <div
                  className="hstack"
                  style={{ gap: 6, marginTop: 8, fontSize: 11, color: 'var(--neg)' }}
                >
                  <Icons.IconAlert size={11} />
                  <span>定时发送时间不能早于当前，请重新选择</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
            营销类推送将按用户的营销开关过滤 · 任务创建记入操作日志
          </span>
          <div className="hstack" style={{ gap: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={pending}>
              取消
            </button>
            {schedule ? (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setSchedule(false)}
                  disabled={pending}
                >
                  改为立即发送
                </button>
                <button
                  type="button"
                  className="btn btn-accent"
                  disabled={submitDisabled}
                  title={submitTitle}
                  onClick={submit}
                >
                  <Icons.IconClock size={11} />
                  确认定时
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={() => setSchedule(true)}
                  disabled={pending}
                >
                  <Icons.IconClock size={11} />
                  定时发送
                </button>
                <button
                  type="button"
                  className="btn btn-accent"
                  disabled={submitDisabled}
                  title={submitTitle}
                  onClick={submit}
                >
                  <Icons.IconPaperPlane size={11} />
                  立即发送
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
